import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createToolBrand,
  updateToolBrand,
  deleteToolBrand,
} from "../../../api/tools";
import { PMField } from "../../utils/PMField";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const ToolBrandDialog = ({
  open,
  onClose,
  brand = null,
  brands = [],
  onBrandCreated,
  onBrandUpdated,
  onBrandDeleted,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const sortedBrands = useMemo(() => {
    return [...(brands || [])].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "nb", {
        sensitivity: "base",
      })
    );
  }, [brands]);

  useEffect(() => {
    if (!open) return;
    if (brand) {
      setEditingBrand(brand);
    } else {
      setEditingBrand(null);
    }
  }, [open, brand]);

  const initialValues = {
    name: editingBrand?.name || "",
    description: editingBrand?.description || "",
    active: editingBrand?.active !== undefined ? editingBrand.active : true,
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t("tool.brand.name") + " " + t("is required"))
      .min(2, t("must be at least 2 characters")),
    description: Yup.string().nullable(),
    active: Yup.boolean(),
  });

  const handleSubmit = async (values, helpers) => {
    setIsLoading(true);
    try {
      let result;
      if (editingBrand) {
        result = await updateToolBrand(editingBrand.id, values);
        toast.success(t("tool.brand.updated"));
        onBrandUpdated?.(result.data);
        setEditingBrand(result.data);
      } else {
        result = await createToolBrand(values);
        toast.success(t("tool.brand.created"));
        onBrandCreated?.(result.data);
        helpers.resetForm({
          values: {
            name: "",
            description: "",
            active: true,
          },
        });
      }
    } catch (error) {
      console.error("Error saving tool brand:", error);
      const msg = error.response?.data?.errors;
      if (Array.isArray(msg)) {
        msg.forEach((m) => toast.error(m));
      } else {
        toast.error(t("Failed to save tool brand"));
      }
    } finally {
      setIsLoading(false);
      helpers.setSubmitting(false);
    }
  };

  const handleDelete = async (b) => {
    const toolsCount = b.tools_count ?? 0;
    const confirmMsg =
      toolsCount > 0
        ? t("tool.brand.delete_confirm_with_tools", { count: toolsCount })
        : t("tool.brand.delete_confirm");
    if (!window.confirm(confirmMsg)) return;

    setIsLoading(true);
    try {
      await deleteToolBrand(b.id);
      toast.success(t("tool.brand.deleted"));
      onBrandDeleted?.(b.id);
      if (editingBrand?.id === b.id) {
        setEditingBrand(null);
      }
    } catch (error) {
      console.error("Error deleting tool brand:", error);
      const apiError =
        error.response?.data?.error || error.response?.data?.errors?.[0];
      toast.error(apiError || t("tool.brand.delete_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const startNew = () => {
    setEditingBrand(null);
  };

  const startEdit = (b) => {
    setEditingBrand(b);
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="tool.brand.manage_title"
      size="5xl"
    >
      <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden md:flex-row md:items-start md:gap-6">
        <aside className="flex w-full shrink-0 flex-col border-b border-gray-200 pb-4 md:w-[260px] md:border-b-0 md:border-r md:pb-0 md:pr-4 lg:w-[280px]">
          <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">
              {t("tool.brand.all_brands")}
            </h4>
            <button
              type="button"
              onClick={startNew}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-600 bg-white px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
            >
              <PlusIcon className="h-4 w-4" />
              {t("tool.brand.new_brand")}
            </button>
          </div>
          <div className="space-y-1">
            {sortedBrands.length === 0 ? (
              <p className="py-4 text-sm text-gray-500">
                {t("tool.brand.no_brands")}
              </p>
            ) : (
              sortedBrands.map((b) => {
                const isActiveRow = editingBrand?.id === b.id;
                return (
                  <div
                    key={b.id}
                    className={`rounded-lg border p-2.5 transition-colors ${
                      isActiveRow
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {b.name}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              b.active
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {b.active ? t("Active") : t("tool.status.inactive")}
                          </span>
                          {typeof b.tools_count === "number" && (
                            <span className="text-[10px] text-gray-500">
                              {t("tool.brand.tools_count", {
                                count: b.tools_count,
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => startEdit(b)}
                          disabled={isLoading}
                          className="rounded p-1.5 text-gray-600 hover:bg-white hover:text-blue-600"
                          title={t("Edit")}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(b)}
                          disabled={isLoading}
                          className="rounded p-1.5 text-gray-600 hover:bg-white hover:text-red-600"
                          title={t("tool.brand.delete")}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        <div className="min-w-0 flex-1 md:pt-0">
          <h4 className="mb-4 text-sm font-semibold text-gray-900">
            {editingBrand
              ? t("tool.brand.edit_brand")
              : t("tool.brand.add_brand")}
          </h4>
          <Formik
            key={editingBrand?.id ?? "new"}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formik) => (
              <Form className="space-y-4">
                <PMField
                  name="name"
                  label={t("tool.brand.name")}
                  type="text"
                  required
                  {...formik}
                />

                <PMField
                  name="description"
                  label={t("tool.brand.description")}
                  multiline
                  rows={3}
                  required
                  {...formik}
                />

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formik.values.active}
                    onChange={formik.handleChange}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    {t("Active")}
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {t("Close")}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || formik.isSubmitting}
                    className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {isLoading || formik.isSubmitting
                      ? editingBrand
                        ? t("Updating...")
                        : t("Creating...")
                      : editingBrand
                      ? t("Update")
                      : t("Create")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </PMDialog>
  );
};

export default ToolBrandDialog;
